import { IDynamicTab } from '../common/dynamic-tabs/models/dynamic-tabs.model';
import { DashboardSorting, SpecialistSearchCriteria, User } from './dashboard-common.model';
import { SingleDayEventData } from './find-a-space.model';
import { IdWithRevision, Item, ItemWithDescription, StatusReason, Space, SpaceFeature } from './masterdata.model';
import { ReservationDetails } from './meetings.model';

export interface BookingDetails {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  setupTime: string;
  tearDownTime: string;
  status: string;
  statusReason: StatusReason;
  revision: number;
  attendeesCount: number;
  magnetAttendeesCount: number;
  actualAttendeesCount: number;
  hasAttendees: true;
  cateringOrderId: number;
  hasCatering: boolean;
  hasCateringContactMeLater: boolean;
  hasAttachments: boolean;
  hasEquipment: boolean;
  hasMaterials: boolean;
  hasComments: boolean;
  hasCommentsAlert: boolean;
  hasTask: boolean;
  isRecurrent: boolean;
  isPrivate: boolean;
  snoozed: boolean;
  owner: User;
  primaryContact: User;
  specialist: User;
  space: Space;
  reservationId: number;
  spaceSetupType: ItemWithDescription;
  eventType: Item;
  syncToOutlook: boolean;
  outlookMeetingId: string;
  eventRegisterStatus: string;
  canCancel: boolean;
  canEdit: boolean;
  canSnooze: boolean;
  canCheckIn: boolean;
  timeZoneAbbreviation: string;
  partOfHybridEvent: boolean;
  bumpTime: string;
  checkedInDate: string;
  createDate: string;
  createBy: User;
  isGhEvent: true;
}

export interface WaitListDashboardRequest {
  Term?: string;
  WorkspaceWaitListBuildingSearchCriterias?: WorkspaceWaitListBuildingSearchCriterias[];
  SpaceTypeSearchCriteriaAny?: boolean | null;
  SpaceTypeSearchCriteriaWorkstation?: boolean | null;
  SpaceTypeSearchCriteriaPrivateSpace?: boolean | null;
  WorkSpaceWaitListSortings?: WorkSpaceWaitListSortings[];
  FloorSearchCriteria?: boolean | null;
  FloorUniqueKeys?: Array<number>;
  EmployeeLevels?: Array<string>;
  WaitListStatusTypes?: Array<string>;
  Features?: string;
  StartDate: string;
  EndDate: string;
  Limit?: number;
  Page?: number;
  Timestamp?: string;
  Status?: Array<string>;
}

export interface WorkSpaceWaitListFloorSearchCriteria {
  any: boolean;
  floorUniqueKeys: number[];
}

export interface WorkspaceWaitListBuildingSearchCriterias {
  buildingId: number;
  workSpaceWaitListFloorSearchCriteria: WorkSpaceWaitListFloorSearchCriteria;
}

export interface WorkSpaceWaitListSortings {
  property: number;
  order: 'Asc';
}

export interface WaitListCreationRequest {
  BuildingId: number;
  StartDate: string;
  EndDate: string;
  FloorUniqueKey: number | null;
  SpaceTypeId: number | null;
}

export interface WaitlistDashboardData {
  totalCount: number;
  items: WaitlistItem[];
}
export interface WaitlistItem {
  id: number;
  owner: {
    id: number;
    firstName: string;
    lastName: string;
    userName: string;
    email: string;
    title: string;
    phoneNumbers: [];
    isActive: true;
    employeeLevel: null;
  };
  floorName: string;
  startDate: string;
  endDate: string;
  createDate: string;
  spaceType: any;
  buildingName: string;
  employeeLevel: any;
  currentPositionInQueue: any;
  waitListStatusTypes: string;
}
export interface ReleaseSpaceBookingInput extends IdWithRevision {
  statusReasonId: number;
  statusComment: string;
}

export interface BookingBySpaceRequest {
  spaceIds: Array<number>;
  startDate: string;
  endDate: string;
}
export enum BookingRoutes {
  dashboard = 'dashboard',
  waitlist = 'waitlist',
}
export interface BookingsDashboardInput {
  limit?: number;
  page?: number;
  term?: string;
  buildingIds?: number[];
  floorUniqueKeys?: number[];
  eventTypeIds?: number[];
  spaceTypeIds?: number[];
  statusTypes?: string[];
  bookingSpecialistSearchCriteria?: SpecialistSearchCriteria;
  reservationSpecialistSearchCriteria?: SpecialistSearchCriteria;
  startDate: string;
  endDate: string;
  bookingDashboardSortings?: DashboardSorting[];
  hasAttachments?: boolean;
  hasCaterings?: boolean;
  hasCommentsWithAlerts?: boolean;
  hasEquipment?: boolean;
  hasMaterials?: boolean;
  hasTask?: boolean;
  features: string;
  teamRequestIds?: string[];
}
export interface BookingsDashboardResponse {
  items: Detail[];
  totalCount: number;
  spaceBookingDashboardFacets: {
    reservationSpecialists: User[];
    spaceBookingSpecialists: User[];
  };
}
export interface Detail {
  spaceBookingId: number;
  reservationId: number;
  spaceBookingName: string;
  startDate: string;
  endDate: string;
  buildingName: string;
  spaceName: string;
  floorName: string;
  spaceType: string;
  spaceBookingSpecialist: User;
  reservationSpecialist: User;
  pointOfContact: User;
  owner: User;
  statusType: string;
  eventTypeName: string;
  partOfHybridEvent: boolean;
  vip: boolean;
  bumpTime: string;
  checkedInDate: string;
  canCancel: boolean;
  snoozed: boolean;
  canSnooze: boolean;
  canEdit: boolean;
  canCheckIn: boolean;
  reservationName: string;
  reservationRevision: number;
  isLtr: boolean;
  hasAttendee: boolean;
  syncToOutlook: boolean;
  timeZoneAbbreviation: string;
  hasCatering: boolean;
  hasTask: boolean;
  hasAttachments: boolean;
  hasEquipment: boolean;
  hasMaterials: boolean;
  hasCommentsAlert: boolean;
  teamName: string;
  revision: number;
  reservationStatusType?:string;
}
export interface ServiceOrdersCalendarItemInput {
  bookingIds?: number[];
  waitListIds?: number[];
  reservationIds?: number[];
  userId?: number;
  hasCateringAssigned?: boolean;
  features?: string;
  limit?: number;
  page?: number;
}
export interface ServiceOrdersCalendarItemResponse {
  id: number;
  statusType: number;
  name: string;
  startDate: string;
  endDate: string;
  type: string;
  cateringAvailability: CateringAvailability;
  cateringOrder: CateringOrder;
}
export interface CateringAvailability {
  status: string;
  closestCateringDate: string;
}
export interface CateringOrder {
  id: number;
  needsReview: boolean;
}
export interface BookingTab extends IDynamicTab {
  status: string;
  time: number;
}
export interface DetailsResponse {
  bookingDetails?: BookingDetails;
  reservationDetails?: ReservationDetails;
  features?: SpaceFeature[];
  serviceOrdersCount?: ServiceOrdersCount;
}
export interface ServiceOrdersCount {
  commentCount: number;
  taskCount: number;
  taskDueCount: number;
  cateringCount: number;
  cateringDueCount: number;
  attachmentCount: number;
  internalAttendeeCount: number;
  externalAttendeeCount: number;
  expenseCount: number;
}
export interface AuditTrailInput {
  referenceType?: string;
  id?: string;
  limit?: number;
  page?: number;
}
export interface AuditTrailResponse {
  items: Items[];
  totalCount: number;
}
export interface Items {
  changeBy: string;
  changeDate: string;
  propertyName: string;
  oldValue: string;
  newValue: string;
  timeZoneId: string;
  taskId?: string;
  createDateUTC?: string;
  timeZoneAbbreviation?: string;
}
export interface EditBooking {
  spaceId?: number;
  name?: string;
  setupTypeId?: number;
  isPrivate?: boolean;
  attendeesCount?: number;
  setupTime?: string;
  tearDownTime?: string;
  singleDayEventData?: SingleDayEventData;
  spaceBookingSpecialistId?: number;
  primaryContactId?: number;
  reservationId?: number;
  revision?: number;
}
export enum BOOKING_AUDIT_TRAIL_PROPS {
  Status = 'Status',
  Setuptype = 'Setup type',
  Attendeescount = 'Attendees count',
  Enddate = 'End date',
  Endtime = 'End time',
  Floor = 'Floor',
  Isprivate = 'Is private',
  Name = 'Name',
  Reservationid = 'Reservation id',
  Roomname = 'Room name',
  Setuptime = 'Setup time',
  Startdate = 'Start date',
  Starttime = 'StartTime',
  Teardowntime = 'Tear down time',
  Bookingspecialist = 'Booking specialist',
  Statusreason = 'Status reason',
  Statuscomment ='Status comment'
}
export const BOOKING_AUDIT_TRAIL_PROPS_NAMES: Map<BOOKING_AUDIT_TRAIL_PROPS, string> = new Map([
  [BOOKING_AUDIT_TRAIL_PROPS.Attendeescount, 'Translation.Attendeescount'],
  [BOOKING_AUDIT_TRAIL_PROPS.Enddate, 'Translation.Enddate'],
  [BOOKING_AUDIT_TRAIL_PROPS.Endtime, 'Translation.End_time'],
  [BOOKING_AUDIT_TRAIL_PROPS.Floor, 'Translation.Floor'],
  [BOOKING_AUDIT_TRAIL_PROPS.Isprivate, 'Translation.Isprivate'],
  [BOOKING_AUDIT_TRAIL_PROPS.Name, 'Translation.Name'],
  [BOOKING_AUDIT_TRAIL_PROPS.Reservationid, 'Translation.ReservationId'],
  [BOOKING_AUDIT_TRAIL_PROPS.Roomname, 'Translation.Roomname'],
  [BOOKING_AUDIT_TRAIL_PROPS.Setuptime, 'Translation.Setuptime'],
  [BOOKING_AUDIT_TRAIL_PROPS.Setuptype, 'Translation.Setuptype'],
  [BOOKING_AUDIT_TRAIL_PROPS.Startdate, 'Translation.Startdate'],
  [BOOKING_AUDIT_TRAIL_PROPS.Starttime, 'Translation.Start_time'],
  [BOOKING_AUDIT_TRAIL_PROPS.Status, 'Translation.Status'],
  [BOOKING_AUDIT_TRAIL_PROPS.Teardowntime, 'Translation.Tear_down_time'],
  [BOOKING_AUDIT_TRAIL_PROPS.Bookingspecialist, 'Translation.Bookingspecialist'],
  [BOOKING_AUDIT_TRAIL_PROPS.Statusreason, 'Translation.Statusreason'],
  [BOOKING_AUDIT_TRAIL_PROPS.Statuscomment, 'Translation.Notes']
])
export const AUDIT_TRAIL_COLUMN_CONFIG = [
  {
    name: 'Translation.Valuechanged',
    flex: '1',
  },
  {
    name: 'Translation.Oldval',
    flex: '1.5',
  },
  {
    name: 'Translation.Newval',
    flex: '1.5',
  },
  {
    name: 'Translation.Changedby',
    flex: '1',
  },
  {
    name: 'Translation.Date_time',
    flex: '1.5',
  },
];
export interface ReservationServiceOrderCount {
  bookingId: string;
  serviceOrderCount: number;
  serviceOrderType: string;
}

export interface ReservationVerifyResult {
  type: ReservationVerifyType;
  message: string;
  maxConcurrentIndividualBookings?: {
    bookings: ReservationVerifyConflictingBooking[];
    canBeAutoResolved: boolean;
  };
}

export interface ReservationVerifyConflictingBooking {
  booking: BookingDetails;
  startDate: string;
  endDate: string;
}

export interface ConflictingSpaceBooking {
  id: number;
  releaseType: ResolveConflictsChoise;
}

export enum ResolveConflictsChoise {
  ReleaseEntire = 'Fully',
  ReleaseConflicting = 'Partially',
  Keep = 'SkipRelease',
}

export enum ReservationVerifyType {
  MaxConcurrentIndividualBookings = 'MaxConcurrentIndividualBookings',
  MaxReservationDays = 'MaxReservationDays',
}

export enum BookingStatus {
  AwaitingCheckIn = 1,
  CheckedIn = 2,
  Reserved = 3,
  PendingApproval = 4,
  AutoBumped = 5,
  Cancelled = 6,
  Conflict = 7,
  Released = 8,
}
